import { Controller } from "@hotwired/stimulus";
import httpService from "../../dashboard/frontend/source/js/services/HttpService";
import channelService from "../../dashboard/frontend/source/js/services/ChannelService";

export default class PaginationController extends Controller {
  static targets = ['pagination'];

  static values = {
    id: String,
    turboID: String,
    current: Number,
    total: Number,
    link: String,
  };

  initialize() {
    // Check if pagination have been generated already.
    // When using browser back function, event handlers will no longer work.
    // So, we need to remove and recreate it.
    const previouslyGenerated = this.paginationTarget.getElementsByTagName("ul").length !== 0;
    if (previouslyGenerated) {
      this.paginationTarget.innerHTML = "";
    }

    this.idValue = this.paginationTarget.getAttribute('id');
    this.turboIDValue = this.paginationTarget.dataset.turboid;
    this.currentValue = Number(this.paginationTarget.dataset.current);
    this.totalValue = Number(this.paginationTarget.dataset.total);
    this.linkValue = this.paginationTarget.dataset.link;
    this.boundClickHandler = this.paginationClickHandler.bind(this);

    if (this.idValue && this.turboIDValue && this.currentValue && this.totalValue && this.linkValue) {
      this.generatePaginationElements();
    } else {
      throw new Error('Pagination component needs a unique id, turboID, link, current and total number of pages!');
    }
  }

  generatePaginationElements() {
    // Generate Pagination array as items.
    const current = this.currentValue;
    const total = this.totalValue;

    if (total === 1) {
      return [];
    }

    const items = [];

    function addPaginationItem(item) {
      if (!items.includes(item)) {
        items.push(item);
      }
    }

    const thirdPrevious = current - 3;
    const secondPrevious = current - 2;
    const previous = current - 1;
    const next = current + 1;
    const secondNext = current + 2;
    const thirdNext = current + 3;

    addPaginationItem(1);
    if (thirdPrevious > 1) addPaginationItem(thirdPrevious);
    if (secondPrevious > 1) addPaginationItem(secondPrevious);
    if (previous > 1) addPaginationItem(previous);
    addPaginationItem(current);
    if (next < total) addPaginationItem(next);
    if (secondNext < total) addPaginationItem(secondNext);
    if (thirdNext < total) addPaginationItem(thirdNext);
    addPaginationItem(total);

    // Generate HTML Elements.
    const paginationList = document.createElement('ul');
    let paginationElement;
    let paginationLink;

    for (let i = 0; i < items.length; i++) {
      paginationElement = document.createElement('li');
      if (this.currentValue === items[i]) paginationElement.classList.add('active');

      paginationLink = document.createElement('a');
      paginationLink.setAttribute('href', `${this.linkValue}${items[i]}`);
      paginationLink.textContent = items[i];

      paginationLink.addEventListener('click', this.boundClickHandler);

      paginationElement.appendChild(paginationLink);
      paginationList.appendChild(paginationElement);
    }

    this.paginationTarget.appendChild(paginationList);
  }

  async paginationClickHandler(event) {
    this.showSpinner();

    event.preventDefault(); // Prevent default href behavior when element is clicked

    // Switch active state to new button.
    const currentlyActiveElement = this.paginationTarget.getElementsByClassName("active")[0];
    currentlyActiveElement.classList.remove("active");
    event.target.parentElement.classList.add("active");

    // Update pagination data with new current value.
    this.currentValue = Number(event.target.textContent);

    const url = event.target.href; // Get the href attribute

    try {
      const res = await httpService.get(url);
      channelService.connect();
      this.generateNewPagination();
      await Turbo.renderStreamMessage(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

showSpinner() {
  const ele = document.getElementById(this.turboIDValue);
  if (!ele) return;

  const rect = ele.getBoundingClientRect();
  const prevH = Math.max(Math.ceil(rect.height || ele.scrollHeight || 0), 40);

  ele.innerHTML = `
    <div class="flex items-center justify-center" style="height:${prevH}px">
      <span class="pagination-spinner"></span>
    </div>
  `;
}

  generateNewPagination() {
    const paginationElements = this.paginationTarget.getElementsByTagName("a");

    for (let i = 0; i < paginationElements.length; i++) {
      paginationElements[i].removeEventListener("click", this.boundClickHandler);
    }

    this.paginationTarget.innerHTML = "";
    this.generatePaginationElements();
  }
}
